import React, { useEffect, useMemo, useState } from "react";
import { gql, useApolloClient, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import "./../styles/Klausuren.css";

const DEFAULT_FACULTY_ID = "1c4e71de-9018-44d3-929e-042704d86516";

const FACULTY_CATEGORIES_QUERY = gql`
  query KlausurenFacultyCategories($facultyId: ID!) {
    facultyCategoriesByFaculty(facultyId: $facultyId) {
      id
      name
      position
    }
  }
`;

const SUBJECTS_BY_CATEGORY_QUERY = gql`
  query KlausurenSubjectsByFacultyCategory($facultyCategoryId: ID!) {
    subjectsByFacultyCategory(facultyCategoryId: $facultyCategoryId) {
      position
      subject {
        id
        title
        slug
        description
        isPublic
      }
      facultyCategory {
        id
      }
    }
  }
`;

const SUBJECT_ALTKLAUSUREN_QUERY = gql`
  query KlausurenSubjectAltklausuren($subjectID: ID!) {
    altklausuren(subjectID: $subjectID) {
      id
      isPublished
      versions {
        id
        isPublished
      }
    }
  }
`;

const ENROLLMENTS_BY_USER_QUERY = gql`
  query KlausurenEnrollmentsByUser($userId: ID!) {
    enrollmentsByUser(userId: $userId) {
      id
      subjectId
    }
  }
`;

const sortByPosition = (a, b) => {
  const av = Number(a?.position);
  const bv = Number(b?.position);
  const safeA = Number.isFinite(av) ? av : Number.MAX_SAFE_INTEGER;
  const safeB = Number.isFinite(bv) ? bv : Number.MAX_SAFE_INTEGER;
  if (safeA !== safeB) return safeA - safeB;
  return String(a?.name || a?.subject?.title || "").localeCompare(
    String(b?.name || b?.subject?.title || "")
  );
};

const countPublishedAltklausuren = (altklausuren = []) =>
  altklausuren.filter((alt) => {
    if (alt?.isPublished === false) return false;
    return (alt?.versions || []).some((version) => version?.isPublished !== false);
  }).length;

const Klausuren = () => {
  const client = useApolloClient();
  const navigate = useNavigate();
  const userId = localStorage.getItem("userID");
  const userFacultyId = localStorage.getItem("userFacultyId") || DEFAULT_FACULTY_ID;

  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [activeFaculty, setActiveFaculty] = useState("all");

  const { data: categoriesData, loading: categoriesLoading } = useQuery(
    FACULTY_CATEGORIES_QUERY,
    {
      variables: { facultyId: userFacultyId },
      fetchPolicy: "network-only",
    }
  );

  const { data: enrollmentsData } = useQuery(ENROLLMENTS_BY_USER_QUERY, {
    variables: { userId: userId || "" },
    skip: !userId,
    fetchPolicy: "network-only",
  });

  const categories = useMemo(
    () => (categoriesData?.facultyCategoriesByFaculty || []).slice().sort(sortByPosition),
    [categoriesData]
  );

  const enrolledSubjectIds = useMemo(
    () => new Set((enrollmentsData?.enrollmentsByUser || []).map((item) => item?.subjectId).filter(Boolean)),
    [enrollmentsData]
  );

  useEffect(() => {
    if (!categories.length) {
      setItems([]);
      return undefined;
    }

    let cancelled = false;

    const loadKlausurenSubjects = async () => {
      setLoadingItems(true);
      setLoadError("");

      try {
        const subjectResults = await Promise.all(
          categories.map((category) =>
            client.query({
              query: SUBJECTS_BY_CATEGORY_QUERY,
              variables: { facultyCategoryId: category.id },
              fetchPolicy: "network-only",
            })
          )
        );

        const subjectMap = new Map();
        subjectResults.forEach((result, index) => {
          const category = categories[index];
          const relations = (result?.data?.subjectsByFacultyCategory || [])
            .slice()
            .sort(sortByPosition);

          relations.forEach((relation) => {
            const subject = relation?.subject;
            if (!subject?.id || !subject?.slug || subject?.isPublic !== true) return;
            if (subjectMap.has(subject.id)) return;

            subjectMap.set(subject.id, {
              ...subject,
              facultyId: category.id,
              facultyName: category.name || "Fakultät",
              position: relation?.position,
            });
          });
        });

        const subjects = Array.from(subjectMap.values());
        const withCounts = await Promise.all(
          subjects.map(async (subject) => {
            try {
              const result = await client.query({
                query: SUBJECT_ALTKLAUSUREN_QUERY,
                variables: { subjectID: subject.id },
                fetchPolicy: "network-only",
              });

              return {
                ...subject,
                klausurenCount: countPublishedAltklausuren(result?.data?.altklausuren || []),
              };
            } catch {
              return {
                ...subject,
                klausurenCount: 0,
              };
            }
          })
        );

        if (cancelled) return;

        setItems(
          withCounts
            .filter((subject) => subject.klausurenCount > 0)
            .sort((a, b) => {
              const facultyCompare = String(a.facultyName || "").localeCompare(String(b.facultyName || ""));
              if (facultyCompare !== 0) return facultyCompare;
              return String(a.title || "").localeCompare(String(b.title || ""));
            })
        );
      } catch (error) {
        if (!cancelled) {
          setLoadError(error?.message || "Klausuren konnten nicht geladen werden.");
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoadingItems(false);
      }
    };

    loadKlausurenSubjects();

    return () => {
      cancelled = true;
    };
  }, [categories, client]);

  const facultyFilters = useMemo(() => {
    const byId = new Map();
    items.forEach((item) => {
      if (!item.facultyId) return;
      byId.set(item.facultyId, item.facultyName || "Fakultät");
    });
    return Array.from(byId.entries()).map(([id, name]) => ({ id, name }));
  }, [items]);

  const visibleItems = useMemo(() => {
    if (activeFaculty === "all") return items;
    return items.filter((item) => item.facultyId === activeFaculty);
  }, [activeFaculty, items]);

  useEffect(() => {
    if (activeFaculty === "all") return;
    if (facultyFilters.some((filter) => filter.id === activeFaculty)) return;
    setActiveFaculty("all");
  }, [activeFaculty, facultyFilters]);

  const openSubjectKlausuren = (subject) => {
    if (!subject?.slug) return;

    if (enrolledSubjectIds.has(subject.id)) {
      navigate(`/subject/${subject.slug}/learn?view=altklausuren`);
      return;
    }

    navigate(`/subject/${subject.slug}`);
  };

  const isLoading = categoriesLoading || loadingItems;

  return (
    <main className="klausuren-page" aria-labelledby="klausuren-title">
      <section className="klausuren-hero">
        <span className="klausuren-eyebrow">Prüfungsarchiv</span>
        <h1 id="klausuren-title">Klausuren.</h1>
        <p>
          Wähle ein Fach und spring direkt zu den verfügbaren Altklausuren. Wenn du noch
          nicht eingeschrieben bist, bringen wir dich zuerst zur Fachseite.
        </p>
      </section>

      <nav className="klausuren-filters" aria-label="Fakultäten filtern">
        <button
          type="button"
          className={activeFaculty === "all" ? "active" : ""}
          onClick={() => setActiveFaculty("all")}
        >
          Alle Fächer
        </button>
        {facultyFilters.map((filter) => (
          <button
            type="button"
            key={filter.id}
            className={activeFaculty === filter.id ? "active" : ""}
            onClick={() => setActiveFaculty(filter.id)}
          >
            {filter.name}
          </button>
        ))}
      </nav>

      {isLoading ? (
        <section className="klausuren-list" aria-label="Klausuren werden geladen">
          {Array.from({ length: 4 }).map((_, index) => (
            <div className="klausuren-row skeleton" key={`klausuren-skeleton-${index}`} />
          ))}
        </section>
      ) : loadError ? (
        <section className="klausuren-empty" role="alert">
          <span>Fehler</span>
          <h2>Klausuren konnten nicht geladen werden</h2>
          <p>{loadError}</p>
        </section>
      ) : visibleItems.length === 0 ? (
        <section className="klausuren-empty">
          <span>Noch leer</span>
          <h2>Keine Klausuren gefunden</h2>
          <p>Für diese Auswahl sind aktuell keine veröffentlichten Altklausuren verfügbar.</p>
        </section>
      ) : (
        <section className="klausuren-list" aria-label="Fächer mit Altklausuren">
          {visibleItems.map((subject) => (
            <article className="klausuren-row" key={subject.id}>
              <div className="klausuren-row-copy">
                <span className="klausuren-faculty">{subject.facultyName}</span>
                <h2>{subject.title}</h2>
                <div className="klausuren-meta">
                  <span>Fakultät: {subject.facultyName}</span>
                  <span>
                    {subject.klausurenCount} {subject.klausurenCount === 1 ? "Altklausur" : "Altklausuren"} verfügbar
                  </span>
                </div>
              </div>

              <button
                type="button"
                className="klausuren-open-btn"
                onClick={() => openSubjectKlausuren(subject)}
                aria-label={`${subject.title} Klausuren öffnen`}
              >
                <span>Ansehen</span>
                <span className="klausuren-arrow" aria-hidden="true">→</span>
              </button>
            </article>
          ))}
        </section>
      )}
    </main>
  );
};

export default Klausuren;
